<script setup lang="ts">
import type { Option } from "@/Modules/Models/Domain/OnBoarding";

import { InfoCircleIcon } from "@/Components/Icons";
import TcTooltip from "@/Components/UI/TcTooltip.vue";

import { isValidString } from "@/Utils/String";

export interface CheckListProps {
  options: Option[];
  selectedValue?: string;
}

const props = defineProps<CheckListProps>();

const emit = defineEmits<{
  (event: "changeSelected", value: string): void;
}>();

const isSelected = (value: string): boolean => value === props.selectedValue;

const isUnselected = (value: string): boolean =>
  isValidString(props.selectedValue) && value !== props.selectedValue;

const changeSelected = (option: Option) => {
  if (!option.disabled) {
    emit("changeSelected", option.value);
  }
};
</script>

<template>
  <ul class="check-list">
    <li
      v-for="option in props.options"
      :key="option.value"
      class="check-list__option"
      :class="{
        selected: isSelected(option.value),
        disabled: option.disabled,
        unselected: isUnselected(option.value),
      }"
      @click="changeSelected(option)"
    >
      <div class="check-list__option__info">
        <div class="check-list__option__info__title">{{ option.title }}</div>
        <div v-if="option.description" class="check-list__option__info__description">
          {{ option.description }}
        </div>
      </div>

      <TcTooltip v-if="option.disabled" activation="hover" :text="option.tooltip || ''" :isVisible="true">
        <InfoCircleIcon color="primary-high" size="md" class="check-list__option__info-icon" />
      </TcTooltip>
    </li>
  </ul>
</template>

<style scoped lang="scss">
.check-list {
  -webkit-tap-highlight-color: transparent;
  display: flex;
  flex-direction: column;
  border: none;
  width: 100%;
  gap: $space-2;
  cursor: pointer;

  &__option {
    @include sp.padding(false, 4);
    @include radius(m);
    @include border(1, solid);
    @include colors.border(content, high);

    display: flex;
    justify-content: center;
    gap: $space-1;

    &__info {
      @include text.modification(center);

      &__title {
        @include colors.color(content, high);
        @include text.modification(center);
        @include text.general(high, xs);
      }

      &__description {
        @include text.general(low, s);
        @include colors.color(content, mid);
      }
    }

    &.selected {
      @include colors.bg(primary, low-solid);
      @include border(1, solid);
      @include colors.border(primary, high);

      transform: translateY(-3px);
    }

    &.unselected {
      @include border(1, solid);
      @include colors.border(content, low-solid);
      @include colors.bg(background, base);

      .check-list__option__info {
        &__title,
        &__description {
          @include colors.color(content, mid);
        }
      }
    }

    &:hover {
      @include colors.bg(primary, low);
      @include border(1, solid);
      @include colors.border(content, high);
    }

    &.disabled {
      @include border(1, solid);
      @include colors.border(content, low-solid);
      @include colors.bg(background, base);

      .check-list__option__info {
        &__title,
        &__description {
          @include colors.color(content, mid);
        }
      }
    }
  }
}
</style>
